import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import "./Home.css";
import 'bootstrap/dist/css/bootstrap.css';

const panelHighlights = [
  {
    title: "Real-Time Console",
    description: "Watch logs live, highlight errors instantly, and keep gameplay running smoothly.",
    mediaType: "image",
    mediaSrc: "/img/panel-preview.webp",
    mediaAlt: "Real-time server console preview",
  },
  {
    title: "Mod Manager",
    description: "Easily discover, install, update, and organize mods without leaving your panel.",
    mediaType: "video",
    mediaSrc: "/vid/mods-preview.webm",
    mediaAlt: "Mod manager preview",
  },
  {
    title: "Plugin Manager",
    description: "Download, update, and manage plugins in a few clicks with simple version control.",
    mediaType: "video",
    mediaSrc: "/vid/plugin-preview.webm",
    mediaAlt: "Plugin manager preview",
  },
];

const exclusiveFeatures = [
  {
    area: "uptime",
    title: "99% Uptime",
    description: "All network outages are covered by our SLA.",
    icon: "/img/time.svg",
    accentClass: "accent-green",
  },
  {
    area: "setup",
    title: "Instant Setup",
    description: "Start hosting in seconds after purchasing your game server.",
    icon: "/img/setup.svg",
    accentClass: "accent-gold",
  },
  {
    area: "support",
    title: "24/7 Support",
    description: "Our support team is available around-the-clock to assist you.",
    icon: "/img/support.svg",
    accentClass: "accent-slate",
  },
  {
    area: "protection",
    title: "DDoS Protection",
    description: "We guarantee full protection against attacks under our SLA.",
    icon: "/img/protection.svg",
    accentClass: "accent-red",
  },
  {
    area: "subdomain",
    title: "Free Subdomain",
    description: "Get a custom IP address using our subdomain creator.",
    icon: "/img/monitoring.svg",
    accentClass: "accent-blue",
  },
  {
    area: "library",
    title: "Expanding Game Library",
    description: "Our team is constantly working to keep our library up to date.",
    icon: "/img/computer.svg",
    accentClass: "accent-purple",
  },
];

const locations = [
  {
    name: "US East",
    country: "USA",
    probeUrl: "https://sky.sodiumhosting.com:8080",
    flag: "https://flagcdn.com/w40/us.png",
  },
  {
    name: "Frankfurt",
    country: "Germany",
    probeUrl: "https://aston-game-ger.sodiumhosting.com:8080",
    flag: "https://flagcdn.com/w40/de.png",
  },
  {
    name: "Australia",
    country: "Australia",
    probeUrl: "https://syd-au-ping.vultr.com",
    flag: "https://flagcdn.com/w40/au.png",
  },
  {
    name: "Japan",
    country: "Japan",
    probeUrl: "https://hnd-jp-ping.vultr.com",
    flag: "https://flagcdn.com/w40/jp.png",
  },
  {
    name: "United Kingdom",
    country: "United Kingdom",
    probeUrl: "https://lon-gb-ping.vultr.com",
    flag: "https://flagcdn.com/w40/gb.png",
  },
];

const measureLatency = (url, timeoutMs = 1200) => new Promise((resolve) => {
  const img = new Image();
  const started = performance.now();
  const timer = setTimeout(() => {
    img.src = "";
    resolve(null);
  }, timeoutMs);

  const finish = () => {
    clearTimeout(timer);
    resolve(Math.round(performance.now() - started));
  };

  img.onload = finish;
  img.onerror = finish;
  img.src = `${url}/favicon.ico?ts=${Date.now()}-${Math.random()}`;
});

export const Home = () => {
  const words = ['YouTube', 'Twitch', 'Community', 'Friends'];
  const [wordIndex, setWordIndex] = useState(0);
  const [letterIndex, setLetterIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [activePanel, setActivePanel] = useState(0);
  const [panelProgress, setPanelProgress] = useState(0);
  const [latencyByLocation, setLatencyByLocation] = useState({});
  const [isPinging, setIsPinging] = useState(false);
  const previewVideoRef = useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/`;
  const description = "Launch game servers in seconds with fast setup, global locations, and a modern control panel built for growing communities.";
  const panelIntervalMs = 120;
  const panelImageDurationMs = 4200;

  useEffect(() => {
    const interval = setInterval(() => {
      setLetterIndex(letterIndex => {
        if (deleting) {
          if (letterIndex === 0) {
            setDeleting(false);
            setWordIndex(wordIndex => (wordIndex + 1) % words.length);
          }
          return letterIndex - 1;
        } else {
          return letterIndex + 1;
        }
      });
    }, 100);

    return () => clearInterval(interval);
  }, [deleting, letterIndex, words, wordIndex]);

  useEffect(() => {
    const timeout = deleting ? 700 : 1500;
    const timer = setTimeout(() => {
      setDeleting(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [deleting]);

  useEffect(() => {
    const activeMedia = panelHighlights[activePanel];
    setPanelProgress(0);

    if (activeMedia.mediaType === "video") {
      const video = previewVideoRef.current;
      if (!video) {
        return undefined;
      }

      let progressTimer = null;

      const updateVideoProgress = () => {
        if (!video.duration || Number.isNaN(video.duration)) {
          return;
        }

        const next = Math.min((video.currentTime / video.duration) * 100, 100);
        setPanelProgress(next);
      };

      const handleLoadedMetadata = () => {
        video.currentTime = 0;
        video.play().catch(() => null);
      };

      const handleEnded = () => {
        setPanelProgress(100);
        setActivePanel(index => (index + 1) % panelHighlights.length);
      };

      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("ended", handleEnded);
      progressTimer = setInterval(updateVideoProgress, panelIntervalMs);

      if (video.readyState >= 1) {
        handleLoadedMetadata();
      }

      return () => {
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("ended", handleEnded);
        clearInterval(progressTimer);
      };
    }

    const interval = setInterval(() => {
      setPanelProgress(progress => {
        const next = progress + (panelIntervalMs / panelImageDurationMs) * 100;
        if (next >= 100) {
          setActivePanel(index => (index + 1) % panelHighlights.length);
          return 0;
        }
        return next;
      });
    }, panelIntervalMs);

    return () => clearInterval(interval);
  }, [activePanel, panelImageDurationMs, panelIntervalMs]);

  const runLatencyCheck = useCallback(async () => {
    setIsPinging(true);
    const latencyResults = {};

    await Promise.all(locations.map(async (location) => {
      const endpoints = [
        location.probeUrl,
        location.probeUrl.replace("https://", "http://"),
      ];

      const samples = [];
      for (let attempt = 0; attempt < 3; attempt += 1) {
        let measured = null;
        for (const endpoint of endpoints) {
          // eslint-disable-next-line no-await-in-loop
          measured = await measureLatency(endpoint);
          if (typeof measured === "number" && measured < 1200) {
            break;
          }
        }
        if (typeof measured === "number" && measured < 1200) {
          samples.push(measured);
        }
      }

      latencyResults[location.name] = samples.length > 0 ? Math.min(...samples) : null;
    }));

    setLatencyByLocation(latencyResults);
    setIsPinging(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      await runLatencyCheck();
      if (!mounted) {
        return;
      }
    };

    run();
    const interval = setInterval(() => {
      runLatencyCheck();
    }, 60000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [runLatencyCheck]);

  const activeHighlight = panelHighlights[activePanel];

  const sortedLocations = useMemo(() => {
    const getLatency = (location) => latencyByLocation[location.name];

    return [...locations].sort((a, b) => {
      const aLatency = getLatency(a);
      const bLatency = getLatency(b);

      if (typeof aLatency !== "number" && typeof bLatency !== "number") {
        return 0;
      }
      if (typeof aLatency !== "number") {
        return 1;
      }
      if (typeof bLatency !== "number") {
        return -1;
      }
      return aLatency - bLatency;
    });
  }, [latencyByLocation]);

    return (
    <div className="home-page">
      <Helmet>
        <title>FlintHost | Game Server Hosting</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="FlintHost | Game Server Hosting" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/img/Logo2.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FlintHost | Game Server Hosting" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${origin}/img/Logo2.png`} />
      </Helmet>
      <section
        className="top hero d-flex flex-column justify-content-center align-items-center"
        style={{ backgroundImage: "url(/img/bg.jpg)" }}
      >
        <div className="hero-content text-center">
          <h1 className="hero-title">Game Server Hosting</h1>
          <div className="typing-text hero-subtitle">
            <h2>for your <u><span>{words[wordIndex].substring(0, letterIndex)}</span></u></h2>
          </div>
          <p className="hero-description">
            High-performance hardware, instant setup, and a control panel built for speed. Launch faster, and keep
            your community online 24/7.
          </p>
          <div className="hero-actions">
            <a className="explore-button" href={"/services"}>Explore Our Services &gt;</a>
            <a className="ghost-button" href={"/minecraft"}>View Minecraft Plans</a>
          </div>
        </div>
        <div className="hero-stats">
          <div className="stat-card">
            <p className="stat-value">99.9%</p>
            <p className="stat-label">Uptime target</p>
          </div>
          <div className="stat-card">
            <p className="stat-value">&lt; 30s</p>
            <p className="stat-label">Average provisioning</p>
          </div>
          <div className="stat-card">
            <p className="stat-value">24/7</p>
            <p className="stat-label">Live support</p>
          </div>
        </div>
      </section>

      <section className="panel-section control-panel-section">
        <div className="control-panel-header">
          <h2 className="section-title">Experience the FlintHost control panel</h2>
        </div>
        <div className="control-panel-body">
          <div className="control-panel-tabs" role="tablist" aria-label="Control panel highlights">
            {panelHighlights.map((highlight, index) => (
              <button
                key={highlight.title}
                type="button"
                className={`panel-tab ${index === activePanel ? "active" : ""}`}
                onClick={() => {
                  setActivePanel(index);
                  setPanelProgress(0);
                }}
                role="tab"
                aria-selected={index === activePanel}
              >
                <span className="panel-tab-title">{highlight.title}</span>
                <span className="panel-tab-description">{highlight.description}</span>
                <span className="panel-tab-progress">
                  <span
                    className="panel-tab-progress-fill"
                    style={{ width: index === activePanel ? `${panelProgress}%` : "0%" }}
                  />
                </span>
              </button>
            ))}
          </div>
          <div className="control-panel-preview" role="tabpanel">
            <div className="control-panel-preview-card">
              <div className="control-panel-placeholder">
                {activeHighlight.mediaType === "video" ? (
                  <video
                    key={activeHighlight.mediaSrc}
                    ref={previewVideoRef}
                    className="control-panel-media"
                    muted
                    playsInline
                    preload="metadata"
                  >
                    <source src={activeHighlight.mediaSrc} type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={activeHighlight.mediaSrc}
                    alt={activeHighlight.mediaAlt}
                    className="control-panel-media"
                  />
                )}
              </div>
              <div className="control-panel-preview-copy">
                <h3>{activeHighlight.title}</h3>
                <p>{activeHighlight.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="panel-section exclusive-features-section">
        <div className="exclusive-features-grid">
          {exclusiveFeatures.map((feature) => (
            <div
              key={feature.title}
              className={`exclusive-card ${feature.accentClass}`}
              style={{ gridArea: feature.area }}
            >
              <div className="exclusive-card-icon">
                <img src={feature.icon} alt="" />
              </div>
              <div className="exclusive-card-content">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </div>
          ))}
          <div className="exclusive-center">
            <h2>Features</h2>
            <p>
              We offer a wide variety of features that enhance your gaming experience and provide powerful hardware
              at the best price.
            </p>
          </div>
        </div>
      </section>

      <section className="panel-section launch-cta-section">
        <div className="launch-cta">
          <div className="launch-cta-header">
            <span className="launch-cta-icon" aria-hidden="true">🚀</span>
            <h2>Ready to get started?</h2>
            <p>
              Whether you want to host a Minecraft server, Hytale, or spin up other game servers, our platform
              has you covered with instant provisioning, high-performance hardware, and 24/7 support.
            </p>
          </div>
          <div className="launch-cta-actions">
            <a className="explore-button" href={"/services"}>Choose Your Game</a>
            <span className="launch-cta-divider">or</span>
            <a className="ghost-button" href={"/contact"}>Contact Us</a>
          </div>
        </div>
      </section>

      <section className="panel-section">
        <h2 className="section-title">Our locations</h2>
        <p className="section-subtitle">Compare regions by live latency to pick the best fit for your players.</p>
        <div className="availability-card">
          <div className="availability-main">
            <div className="availability-table-wrap">
              <table className="availability-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Ping</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLocations.map((location) => {
                    const latency = latencyByLocation[location.name];
                    return (
                      <tr key={location.name}>
                        <td>
                          <span className="location-cell">
                            <img className="flag-icon" src={location.flag} alt={`${location.country} flag`} />
                            {location.name}
                          </span>
                        </td>
                        <td>
                          <span className="ping-pill">
                            {typeof latency === "number" ? `~${latency} ms` : "Unavailable"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <aside className="availability-side-note">
              <h3>Region guidance</h3>
              <p>
                Lower ping is better. Regions are sorted from lowest to highest latency.
              </p>
              <p>
                Ping is measured from your browser requests, so real in-game latency is often lower. Use this as guidance when choosing a region.
              </p>
              <p>
                Auto-refreshed every minute from your browser.
              </p>
            </aside>
          </div>
          <div className="availability-actions">
            <button
              type="button"
              className="ghost-button reping-button"
              onClick={runLatencyCheck}
              disabled={isPinging}
            >
              {isPinging ? "Re-pinging..." : "Re-ping locations"}
            </button>
            <a className="ghost-button" href="/hardware">View full hardware specs</a>
          </div>
        </div>
      </section>
    </div>
  );
};
